import { Ref, ComputedRef } from 'vue';
import Analytics from '@monolith/legacy/services/analytics';
import UserClick from '@monolith/legacy/services/analytics/events/user-click.event';
import ViewTile from '@monolith/legacy/services/analytics/events/view-tile.event';
import { AdsClickEvent, getNativeAdClickOrigin, trackProductClickWithAardvarkService } from '@bc/advertisement';
import ProductUnlikedEvent from '@monolith/legacy/services/analytics/events/product-unliked-event';
import type { ESProductHit, ESSponsoredProductHit } from '../catalog';
import type { ProductProperty } from '@monolith/legacy/services/analytics/properties/product-property';
import type { BrandProperty } from '@monolith/legacy/services/analytics/properties/brand-property';
import { ProductQuickAddToCartEvent } from '@monolith/legacy/services/analytics/events/product-quick-added-to-cart.event';
import { getCustomTrackMergeFn, IdAlgo, IdPosition, SectionType } from '@bc/shared/tracking';
import { SnippetItemType } from './enums';
import type Product from '@monolith/legacy/types/product';
import Store from '@monolith/legacy/store';

type ESAnyProductHit = Product | ESProductHit | ESSponsoredProductHit;

enum ProductSnippetActions {
  CLICKED = 'product_clicked',
  BRAND_CLICKED = 'brand_clicked',
  LIKED = 'product_liked',
  ADS_TOOLTIP_CLICKED = 'ads_tooltip_clicked',
  QATC_CLICKED = 'quick_add_to_cart_clicked',
}

type ProductLocation = {
  position?: number;
  page?: number;
};

/**
 * Fine-grained click descriptors (PUR-3216): the concrete element clicked and its
 * visible label, sent alongside `component`/`action` for deeper funnel analysis.
 */
type ElementInfo = {
  element_type?: string;
  element_label?: string;
};

// `component` must never be empty (PUR-3216): dashboards rely on it always carrying a surface.
const UNKNOWN_COMPONENT = 'unknown';

const getAdId = (product: ESAnyProductHit) => {
  if ('aardvarkServeId' in product) {
    return product.aardvarkServeId;
  }

  if ('ad' in product) {
    return product.ad?.metadata?.ad_id;
  }

  return undefined;
};

export const getProductSnippetTracking = (
  sectionId: Ref<string> | ComputedRef<string>,
  idAlgo: Ref<IdAlgo> | ComputedRef<IdAlgo>,
  component?: Ref<string> | ComputedRef<string>,
  // Origin/parent surface when the tile is nested (e.g. a tile inside a carousel
  // inside the homepage). Captured as `parent_component` for unambiguous attribution (PUR-3216).
  source?: Ref<string | undefined> | ComputedRef<string | undefined>
) => {
  const mergeViewEvents = getCustomTrackMergeFn(['position', 'id_product']);
  // Resolve the surface, guaranteeing `component` is never empty.
  const resolveComponent = (adId?: string) => (adId ? 'native_ads' : component?.value || UNKNOWN_COMPONENT);
  const parentComponent = () => source?.value;
  const trackProductClicked = async (
    payload: {
      product: ESAnyProductHit;
      eventTarget?: Element;
    } & ProductLocation &
      ElementInfo
  ) => {
    Store.dispatch('tracking/setFromSectionId', sectionId.value);
    const adId = getAdId(payload.product);
    await Analytics.track(
      new UserClick({
        action: ProductSnippetActions.CLICKED,
        component: resolveComponent(adId),
        parent_component: parentComponent(),
        element_type: payload.element_type,
        element_label: payload.element_label,
        id_section: sectionId.value,
        id_algo: idAlgo.value,
        itemType: SnippetItemType.PRODUCT,
        page_number: payload.page,
        position: payload.position,
        productId: payload.product.id,
      })
    );
    if (adId) {
      await trackProductClickWithAardvarkService(adId, getNativeAdClickOrigin(payload.eventTarget));
    }
  };

  const trackProductBrandClicked = async (
    payload: {
      product: ESAnyProductHit;
    } & ProductLocation &
      ElementInfo
  ) => {
    const adId = getAdId(payload.product);
    await Analytics.track(
      new UserClick({
        action: ProductSnippetActions.BRAND_CLICKED,
        brandId: payload.product.brand.id,
        brandTags: payload.product.brand.brand_tags,
        component: resolveComponent(adId),
        parent_component: parentComponent(),
        element_type: payload.element_type,
        element_label: payload.element_label,
        id_section: sectionId.value,
        id_algo: idAlgo.value,
        itemType: SnippetItemType.PRODUCT,
        page_number: payload.page,
        position: payload.position,
        productId: payload.product.id,
      })
    );
  };

  const trackProductLiked = async (
    payload: {
      product: ESAnyProductHit;
      id_product?: IdPosition[];
      id_brand?: IdPosition[];
      isSponsored?: boolean;
    } & ProductLocation
  ) => {
    const adId = getAdId(payload.product);
    await Analytics.track(
      new UserClick({
        action: ProductSnippetActions.LIKED,
        component: resolveComponent(adId),
        parent_component: parentComponent(),
        id: payload.product.id,
        id_brand: payload.id_brand,
        id_product: payload.id_product,
        id_section: sectionId.value,
        itemType: SnippetItemType.PRODUCT,
        position: payload.position,
        id_algo: idAlgo.value,
      })
    );
  };

  const trackProductDisliked = async (payload: { product: ESAnyProductHit }) => {
    await Analytics.track(
      new ProductUnlikedEvent(
        {
          id: payload.product.id,
          name: payload.product.name as string,
          wholesalePrice: payload.product.wholesale_price,
          uuid: payload.product.uuid,
        },
        sectionId.value
      )
    );
  };

  const trackQuickAddToCartClicked = async (payload: { product: ESAnyProductHit } & ProductLocation & ElementInfo) => {
    const adId = getAdId(payload.product);
    await Analytics.track(
      new UserClick({
        action: ProductSnippetActions.QATC_CLICKED,
        itemType: 'product',
        component: resolveComponent(adId),
        parent_component: parentComponent(),
        element_type: payload.element_type,
        element_label: payload.element_label,
        id_section: sectionId.value,
        id_algo: idAlgo.value,
        page_number: payload.page,
        productId: payload.product.id,
        position: payload.position,
      })
    );
  };

  const trackVariantAddedToCart = async (
    payload: {
      productProperty: ProductProperty;
      brandProperty: BrandProperty;
      quantity: number;
    } & ProductLocation
  ) => {
    await Analytics.track(
      new ProductQuickAddToCartEvent(
        payload.productProperty,
        payload.brandProperty,
        payload.page,
        payload.quantity,
        sectionId.value,
        idAlgo.value,
        // section_position kept undefined; surface attribution carried by component/source.
        undefined,
        resolveComponent(),
        parentComponent()
      )
    );
  };

  const trackProductViewed = (
    payload: {
      product: ESAnyProductHit;
      valueProposition: string;
      sectionType: SectionType;
    } & ProductLocation
  ) => {
    Analytics.trackMergeBuffer(
      new ViewTile({
        id_section: sectionId.value,
        id_product: [
          {
            id: payload.product.id,
            position: payload.position,
          },
        ],
        position: payload.position,
        value_proposition: payload.valueProposition,
        section_type: payload.sectionType,
      }),
      mergeViewEvents
    );
  };

  const trackSponsoredProductTooltipClick = async (
    payload: {
      product: ESSponsoredProductHit;
    } & ProductLocation
  ) => {
    await Analytics.track(
      new AdsClickEvent({
        sectionId: sectionId.value,
        component: 'native_ads',
        itemtype: SnippetItemType.PRODUCT,
        action: ProductSnippetActions.ADS_TOOLTIP_CLICKED,
        productId: payload.product.id,
        page_number: payload.page,
        position: payload.position,
      })
    );
  };

  return {
    trackProductClicked,
    trackProductBrandClicked,
    trackProductLiked,
    trackProductDisliked,
    trackQuickAddToCartClicked,
    trackVariantAddedToCart,
    trackProductViewed,
    trackSponsoredProductTooltipClick,
  };
};
